import { endOfDay, startOfDay } from 'date-fns';

export type QuarterYear = { quarter: number; year: number };

export const stringToTime = (dateString: string | undefined, showMonth = false) => {
  if (!dateString) {
    return '';
  }
  const date = new Date(dateString);
  return dateFormat(date, showMonth);
};

export const unixTimestampToTime = (unixTimestamp: number | undefined) => {
  if (unixTimestamp) {
    const date = new Date(unixTimestamp * 1000);

    return dateFormat(date);
  } else {
    return '';
  }
};

export const dateFormat = (date: Date, showMonth = false) =>
  date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    month: showMonth ? 'short' : undefined,
    day: showMonth ? 'numeric' : undefined
  });

export const isoToEpoch = (isoString: string): number => new Date(isoString).getTime();

export const calcMinutesToTimestamp = (targetUnixTimestamp: number) => {
  const now = Math.floor(Date.now() / 1000);

  // Calculate the difference in seconds
  const differenceInSeconds = targetUnixTimestamp - now;

  // Convert the difference to minutes
  const differenceInMinutes = Math.floor(differenceInSeconds / 60);

  return differenceInMinutes;
};

export const getQuarterTimestamps = (
  year: number,
  quarter: number,
  firstMonthOfYear = 1
): { from: number; to: number } => {
  if (quarter < 1 || quarter > 4) {
    throw new Error('Quarter must be between 1 and 4.');
  }

  // Calculate the starting month of the fiscal year
  const startMonthOfQuarter = firstMonthOfYear + (quarter - 1) * 3;

  // Wrap around for years that span across January
  const adjustedYear = startMonthOfQuarter > 12 ? year + 1 : year;
  const adjustedStartMonth = ((startMonthOfQuarter - 1) % 12) + 1;

  // Calculate the ending month of the quarter
  const endMonthOfQuarter = adjustedStartMonth + 2;

  const fromDate = startOfDay(new Date(adjustedYear, adjustedStartMonth - 1, 1));
  const toDate = endOfDay(new Date(adjustedYear, endMonthOfQuarter - 1 + 1, 0));

  return {
    from: Math.floor(fromDate.getTime()),
    to: Math.floor(toDate.getTime())
  };
};

export const getCurrentQuarterTimestamps = (firstMonthOfYear = 1): { from: number; to: number } => {
  const now = new Date();
  const year = now.getFullYear();

  // Calculate the fiscal month offset
  const fiscalOffset = now.getMonth() + 1 - firstMonthOfYear;
  const adjustedYear = fiscalOffset < 0 ? year - 1 : year;

  // Determine the quarter based on the fiscal offset
  const quarter = Math.floor(((fiscalOffset + 12) % 12) / 3) + 1;

  return getQuarterTimestamps(adjustedYear, quarter, firstMonthOfYear);
};

export const getCurrentQuarterAndYear = (firstMonthOfYear = 1): QuarterYear => {
  const now = new Date();
  const currentMonth = now.getMonth() + 1; // JavaScript months are 0-indexed
  const year = now.getFullYear();

  // Calculate the fiscal offset
  const fiscalOffset = currentMonth - firstMonthOfYear;

  // Adjust the year for dates before the fiscal year's start month
  const adjustedYear = fiscalOffset < 0 ? year - 1 : year;

  // Calculate the current quarter
  const quarter = Math.floor(((fiscalOffset + 12) % 12) / 3) + 1;

  return { quarter, year: adjustedYear };
};

export const getYearAndQuarter = (date: Date, firstMonthOfYear = 1): QuarterYear => {
  const currentMonth = date.getMonth() + 1; // JavaScript months are 0-indexed
  const year = date.getFullYear();

  // Calculate the fiscal offset
  const fiscalOffset = currentMonth - firstMonthOfYear;

  // Adjust the year for dates before the fiscal year's start month
  const adjustedYear = fiscalOffset < 0 ? year - 1 : year;

  // Calculate the current quarter based on the fiscal offset
  const quarter = Math.floor(((fiscalOffset + 12) % 12) / 3) + 1;

  return { year: adjustedYear, quarter };
};

export const getHoursAndMinutesFromNumberOfSeconds = (seconds: number | undefined) => {
  if (seconds !== undefined) {
    const totalSeconds = Math.floor(seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const remainingSeconds = totalSeconds % 60;

    const paddedHours = hours < 10 ? `0${hours}` : hours;
    const paddedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    const paddedSeconds = remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds;

    return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
  }
  return '';
};
export const getDateStartAndEndDay = (date: Date) => ({
  from: startOfDay(date).getTime(),
  to: endOfDay(date).getTime()
});

export enum DateCategory {
  Today = 'Today',
  Yesterday = 'Yesterday',
  LastWeek = 'Last Week',
  LastMonth = 'Last Month'
}

export const classifyDate = (createdAt: string, categories: string[]): string => {
  const inputDate = new Date(createdAt);
  const now = new Date();

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfToday.getDate() - 1);

  const startOfLastWeek = new Date(startOfToday);
  startOfLastWeek.setDate(startOfToday.getDate() - 7);

  if (inputDate >= startOfToday) {
    return DateCategory.Today;
  } else if (inputDate >= startOfYesterday) {
    return DateCategory.Yesterday;
  } else if (inputDate >= startOfLastWeek) {
    return DateCategory.LastWeek;
  } else {
    return DateCategory.LastMonth;
  }
};

export const formatPrettyDate = (isoDateStr: string): string => {
  if (isoDateStr === '') {
    return '';
  }
  const safeIso = isoDateStr.replace(/([+-]\d{2}):?(\d{2})$/, '$1$2');

  const date = new Date(safeIso);
  if (isNaN(date.getTime())) {
    throw new Error(`Invalid date string: ${isoDateStr}`);
  }

  const day = date.getUTCDate();
  const month = date.toLocaleString('default', { month: 'long', timeZone: 'UTC' });
  const year = date.getUTCFullYear();

  const getOrdinalSuffix = (n: number): string => {
    if (n > 3 && n < 21) return 'th';
    switch (n % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  return `${day}${getOrdinalSuffix(day)} ${month}, ${year}`;
};
